import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';
import { hasFeature } from 'root/api/admin/features';
import Textarea from 'root/apps/cirrus/Textarea';
import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';
import { useSale } from 'root/apps/QuoteMachineIntegration/hooks/useSale';
import VoidService from 'root/apps/QuoteMachineIntegration/services/VoidService';
import Modules from 'root/apps/shared_modules/Modules';
import Monitoring from 'root/apps/shared_modules/Monitoring';

import { StyledAlert, StyledBoxHeading, StyledContentBox } from './index.style';

export const VOID_SALE_LINK_DOC_URL =
  'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/229130888-Voiding-a-sale';

interface VoidSalePageProps {
  saleID: number;
  saleHasAssociatedRefunds: boolean;
  isVoidAllowed: boolean;
  isSystemAdminAccount: boolean;
  willVoidRefundPayments: boolean;
  saleHasDepositByPaylink: boolean;
}

const VoidNotAllowedSection = ({ isSystemAdminAccount }: { isSystemAdminAccount: boolean }) => {
  return (
    <Flex flexDirection="column" data-test="voiding-sale-not-allowed-action-list">
      {isSystemAdminAccount ? (
        <Alert type="warning" noCloseBtn mb={2} data-test="system-admin-only-void-warning">
          <Text fontWeight="bold">This is a SYS ADMIN only void</Text>{' '}
        </Alert>
      ) : null}
      <Text fontWeight="bold">
        This sale cannot be voided because of one of the following reasons:
      </Text>
      <ul>
        <li>The sale is incomplete.</li>
        <li>The sale contains a credit card refund or void.</li>
        <li>The sale contains a gift card purchase where the gift card has been redeemed.</li>
        <li>The sale contains a charge that is over 119 days old.</li>
        <li>
          The sale has been processed through Lightspeed Payments. A refund will need to be
          completed to return payment to the cardholder.
        </li>
      </ul>
    </Flex>
  );
};

export const ERROR_TYPES = {
  GENERIC: 'Something went wrong. Try again.',
  COMPLETING_VOID: 'Error while voiding the sale. Try again.',
  HAS_ASSOCIATED_REFUNDS:
    'This sale cannot be voided as it has already been refunded. To void this sale, all refunds associated with this transaction will need to be voided first.',
};

const VoidSalePage: React.FC<VoidSalePageProps> = ({
  saleID,
  saleHasAssociatedRefunds,
  isVoidAllowed,
  isSystemAdminAccount,
  willVoidRefundPayments,
  saleHasDepositByPaylink,
}) => {
  const [reasonForVoiding, setReasonForVoiding] = React.useState('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState<string>('');
  const [invoicingAllowsVoid, setInvoicingAllowsVoid] = React.useState(true);
  const [invoicingErrorMessage, setInvoicingErrorMessage] = React.useState<string>('');

  // Fetch sale data to check for credit account
  const { sale, loading: loadingSale } = useSale(String(saleID));

  React.useEffect(() => {
    if (isSystemAdminAccount && !isVoidAllowed) {
      setReasonForVoiding('Lightspeed Retail System Administrator Void: ');
    }

    if (saleHasAssociatedRefunds) {
      setIsError(ERROR_TYPES.HAS_ASSOCIATED_REFUNDS);
    }
  }, [isSystemAdminAccount, isVoidAllowed, saleHasAssociatedRefunds]);

  // Check if invoicing allows void
  React.useEffect(() => {
    // Wait for sale data to load
    if (loadingSale) {
      return;
    }

    // Only consult invoicing for merchants that have the integration enabled
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return;
    }

    // Extract credit account ID from sale
    const creditAccountId = sale?.Customer?.creditAccountID;

    const checkInvoicingAllowsVoid = async () => {
      if (!(await hasFeature('InvoicingApiV1'))) {
        return true;
      }
      const result = await VoidService.checkInvoicingAllowsVoid(saleID, creditAccountId);
      setInvoicingAllowsVoid(result.isAllowed);
      setInvoicingErrorMessage(result.errorMessage);
      return result;
    };

    checkInvoicingAllowsVoid();
  }, [saleID, loadingSale, sale]);

  const VOID_ACTION_ITEMS = [
    'Deletes the sale and its payments from Reports and Customer Records',
    'Adds the Sale to the Voids Report',
    'Returns the Sale Items back to Inventory',
    willVoidRefundPayments
      ? "Returns money directly to the Customer's card"
      : "Will not return money to the Customer's card",
  ];

  const getCreditAccountSalePayments = () => {
    const CREDIT_ACCOUNT_PAYMENT_TYPE_CODE = 'SCA';
    const CREDIT_ACCOUNT_PAYMENT_TYPE_ID = 4;
    const salePayments = sale?.SalePayments?.SalePayment;

    if (!salePayments) {
      return [];
    }

    const paymentsArray = Array.isArray(salePayments) ? salePayments : [salePayments];

    return paymentsArray.filter((payment: SalePayment) => {
      const isCreditAccount = payment.PaymentType?.code
        ? payment.PaymentType.code === CREDIT_ACCOUNT_PAYMENT_TYPE_CODE
        : Number(payment.PaymentType?.paymentTypeID) === CREDIT_ACCOUNT_PAYMENT_TYPE_ID;
      return isCreditAccount && (!payment.archived || payment.archived === 'false');
    });
  };

  const createVoidIntent = async () => {
    const creditAccountId = sale?.Customer?.creditAccountID;
    const creditAccountPayments = getCreditAccountSalePayments();

    // Only call void-intent if there are credit account sale payments
    if (!creditAccountId || creditAccountPayments.length === 0) {
      return;
    }

    // Calculate total amount of credit account sale payments
    const totalAmount = creditAccountPayments.reduce(
      (sum: number, payment: SalePayment) => sum + Number(payment.amount || 0),
      0
    );
    const amount = totalAmount.toString();
    const currency = window.merchantos.locale.currency_code;

    const response = await VoidService.createVoidIntent(saleID, creditAccountId, amount, currency);

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to create void intent: ${response.status} ${response.statusText}`);
    }
  };

  const confirmVoidSale = async () => {
    setIsConfirmationModalOpen(false);
    setIsLoading(true);

    const args = {
      name: !isVoidAllowed && isSystemAdminAccount ? 'sysadmin_void_sale_fnc' : 'void_sale_fnc',
      pannel_id: 'view',
      context: 'view',
      confirm_msg: '',
      prevent_repeat: false,
      extra: null,
    };

    try {
      if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
        await createVoidIntent();
      }

      const voidReasonEL = document.querySelector(
        '[name="void_reason"]'
      ) as HTMLInputElement | null;
      if (!voidReasonEL) throw new Error('Could not find void_reason input element');
      voidReasonEL.value = reasonForVoiding;

      await window.merchantos.controls.customFunctionOnClick(
        document.getElementById('voidSaleButton')!,
        args
      );
    } catch (err) {
      setIsError(ERROR_TYPES.COMPLETING_VOID);
      Monitoring.addEvent(err, 'VoidSale', {
        errorName: 'sale.void_sale_error',
        description: `Error while trying to void sale ${saleID}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const VOID_SALE_LINK = useLocalizedLink(VOID_SALE_LINK_DOC_URL);

  return (
    <>
      {saleHasDepositByPaylink ? (
        <Box maxWidth="1000px" mb={2} lineHeight={1.4}>
          <StyledAlert type="warning" noCloseBtn data-test="sale-has-remote-deposit-warning">
            A remote deposit was generated from this transaction. If a payment was received through
            a payment link, an additional sales record has been created.{' '}
            <Text fontWeight="bold">
              Voiding this transaction will not reimburse the deposit or remove the other sales
              record.
            </Text>
          </StyledAlert>
        </Box>
      ) : null}
      <Modal
        isOpen={isConfirmationModalOpen}
        onRequestClose={() => setIsConfirmationModalOpen(false)}
      >
        <ModalHeader
          onCloseClicked={() => setIsConfirmationModalOpen(false)}
          data-test="void-sale-confirmation-modal"
        >
          Void sale{saleID && <var className="notranslate"> #{saleID}</var>}?
        </ModalHeader>
        <ModalBody>
          <Flex>
            <Text>This action is permanent and can&apos;t be undone.</Text>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex justifyContent="right">
            <Button
              onClick={() => setIsConfirmationModalOpen(false)}
              id="cancel-button"
              data-test="void-sale-cancel-button"
            >
              Cancel
            </Button>
            <Button
              id="void-sale-confirm"
              data-test="void-sale-confirm"
              variant="danger"
              fill
              ml={2}
              onClick={() => confirmVoidSale()}
            >
              Void sale
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>
      <Flex flexDirection="column" maxWidth="540px">
        {!!isError && (
          <Alert type="danger" mb={2}>
            {isError}
          </Alert>
        )}
        {!!invoicingErrorMessage && (
          <Alert type="danger" mb={2}>
            {invoicingErrorMessage}
          </Alert>
        )}
        <StyledBoxHeading>
          <Text>Voiding a Sale</Text>
        </StyledBoxHeading>
        <StyledContentBox>
          {!isVoidAllowed ? (
            <VoidNotAllowedSection isSystemAdminAccount={isSystemAdminAccount} />
          ) : (
            <ul
              style={{ margin: '0px', paddingLeft: '18px' }}
              data-test="voiding-sale-allowed-action-list"
            >
              {VOID_ACTION_ITEMS.map((voidActionItem) => (
                <li key={`${voidActionItem}`}>{voidActionItem}</li>
              ))}
            </ul>
          )}
        </StyledContentBox>
        <StyledBoxHeading>
          <Text>Important notice</Text>
        </StyledBoxHeading>
        <StyledContentBox>
          <Flex alignItems="center">
            <Box>
              <IconWarning size="30px" />
            </Box>
            <Box ml="12px">
              <Text mb="6px" data-test="void-sale-important-notice-content">
                Voiding a sale is permanent and cannot be undone. Please use cautiously.
              </Text>
              <TextLink href={VOID_SALE_LINK} target="_blank">
                Learn more about voiding sales
                <IconExternalLink color="#2E61DE" />
              </TextLink>
            </Box>
          </Flex>
        </StyledContentBox>
        <Flex flexDirection="column" mb="12px">
          <Textarea
            value={reasonForVoiding}
            rows={5}
            cols={12}
            onChange={(event: { target: { value: React.SetStateAction<string> } }) =>
              setReasonForVoiding(event.target.value)
            }
            data-test="reason-for-voiding-text-area"
            id="reason-for-voiding-text-area"
            label="Reason for voiding"
            labelHelper={null}
            description={null}
            textHelper={null}
            status={null}
            disabled={isLoading || !!isError || (!isVoidAllowed && !isSystemAdminAccount)}
            readOnly={isLoading || !!isError || (!isVoidAllowed && !isSystemAdminAccount)}
            autoFocus
            style={{ resize: 'none' }}
          />
        </Flex>
        <Button
          onClick={() => setIsConfirmationModalOpen(true)}
          disabled={
            isLoading ||
            !!isError ||
            (!isVoidAllowed && !isSystemAdminAccount) ||
            !invoicingAllowsVoid
          }
          data-test="void-sale-button"
        >
          Void sale
        </Button>
      </Flex>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: VoidSalePage,
  selector: '[data-react-root="VoidSalePage"]',
});

export default VoidSalePage;

history.onChangeState(
  [
    {
      form_name: 'view',
      tab: 'void',
      name: 'transaction.views.transaction',
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
